import React from 'react';
import { Close as CloseIcon, Build as BuildIcon } from '@mui/icons-material';

/**
 * MainteSOPChecklist Component (Demonstration Edition)
 * specialized for maintenance procedures when statusId is 11.
 */

const MainteSOPChecklist = ({ isOpen, onClose, machineName }) => {
  // Demo data as requested
  const checklist = [
    { id: 1, text: '작업허가서 승인', is_checked: 2 },
    { id: 2, text: '개인보호구 착용', is_checked: 2 },
    { id: 3, text: '주 전원 차단', is_checked: 2 },
    { id: 4, text: 'LOTO 체결 및 등록', is_checked: 3 }, // Violation!
    { id: 5, text: '정비 시작', is_checked: 2 },
    { id: 6, text: '정비 종료', is_checked: 0 },
    { id: 7, text: 'LOTO 해제', is_checked: 0 },
  ];

  const getStatusConfig = (status) => {
    switch (status) {
      case 2:
        return {
          bg: 'bg-indigo-500/10',
          borderColor: 'rgba(99, 102, 241, 0.4)',
          badge: 'bg-indigo-600 text-white',
          dot: 'bg-indigo-500',
          label: '완료',
        };
      case 3: // Violation
        return {
          bg: 'bg-red-500/10',
          borderColor: '#ef4444', // Static high contrast
          badge: 'bg-red-600 text-white font-black',
          dot: 'bg-red-500',
          label: '절차 위반',
          extraClass: 'border-2 shadow-[0_0_15px_rgba(239,68,68,0.2)]',
          useStripes: true
        };
      default:
        return {
          bg: 'bg-slate-800/20',
          borderColor: 'rgba(255, 255, 255, 0.05)',
          badge: 'bg-slate-700 text-slate-400',
          dot: 'bg-slate-600',
          label: '미처리',
        };
    }
  };

  const progressCount = checklist.filter(i => i.is_checked === 2).length;

  if (!isOpen) return null;

  return (
    <div id="mainte-sop-checklist" className="fixed top-0 left-[80px] w-[380px] h-full z-[1400] flex flex-col pointer-events-none will-change-transform transform-gpu font-['Pretendard']">
      <div className="flex-1 flex flex-col bg-slate-950/95 backdrop-blur-[6px] border-r border-slate-800 shadow-[20px_0_50px_rgba(0,0,0,0.6)] pointer-events-auto animate-sop-entry overflow-hidden">

        {/* Header Section */}
        <div className="px-4 py-3 bg-slate-900 border-b border-white/5 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-orange-500 shadow-[0_0_10px_#f97316]" />
              <span className="text-[11px] font-black tracking-widest text-orange-500 uppercase">Maintenance SOP</span>
            </div>
            <h2 className="text-xl font-black text-white flex items-center gap-2 tracking-tight">
              <BuildIcon sx={{ fontSize: 24, color: '#6366f1' }} />
              설비보전 SOP
            </h2>
            <p className="text-[11px] text-slate-500 font-bold mt-1">대상: {machineName || '알 수 없는 설비'}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 transition-all"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Checklist Content */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3 sop-scrollbar">
          {checklist.map((item, index) => {
            const config = getStatusConfig(item.is_checked);
            const isDone = item.is_checked === 2;

            return (
              <div
                key={item.id}
                className={`relative px-4 py-3 rounded-xl ${config.bg} ${config.extraClass || ''} transition-all duration-200 flex flex-col gap-1 overflow-hidden shadow-inner`}
                style={{ border: '1px solid ' + config.borderColor }}
              >
                {config.useStripes && (
                  <div
                    className="absolute inset-y-0 left-0 w-1 opacity-50"
                    style={{
                      backgroundImage: 'repeating-linear-gradient(45deg, #ef4444, #ef4444 5px, transparent 5px, transparent 10px)'
                    }}
                  />
                )}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${config.dot}`} />

                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded shadow-sm ${config.badge}`}>
                      {config.label}
                    </span>
                    <span className="text-[11px] font-black text-slate-400 tracking-tighter">
                      STEP {index + 1}
                    </span>
                  </div>
                  {isDone && (
                    <div className="w-5 h-5 rounded bg-indigo-600 flex items-center justify-center shadow-[0_0_8px_rgba(99,102,241,0.5)]">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>

                <h3 className={`text-[13px] font-bold leading-snug tracking-tight ${isDone ? 'text-slate-500 line-through decoration-slate-600' : 'text-slate-100'}`}>
                  {item.text}
                </h3>
              </div>
            );
          })}
        </div>

        {/* Footer Section */}
        <div className="px-3 py-4 bg-slate-900 border-t border-white/5 shrink-0 space-y-4">
          {/* Violation Banner */}
          <div className="relative overflow-hidden bg-red-500/10 border-2 border-red-500 rounded-lg p-2.5 flex items-start gap-2.5">
            {/* 왼쪽 혹은 배경 일부에 위험 빗금 패턴 추가 */}
            <div
              className="absolute inset-y-0 left-0 w-1 opacity-50"
              style={{
                backgroundImage: 'repeating-linear-gradient(45deg, #ef4444, #ef4444 5px, transparent 5px, transparent 10px)'
              }}
            />
            <div className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(220,38,38,0.8)]">
              <span className="text-[12px] font-black text-white">!</span>
            </div>
            <div className="flex-1">
              <p className="text-[11px] font-black text-red-500 tracking-tight leading-tight">절차 위반 감지 (Critical Violation)</p>
              <p className="text-[10px] text-slate-400 font-bold mt-0.5 leading-snug break-keep">
                4단계(LOTO 체결) 미이행 상태에서 5단계(정비 시작)가 수행되었습니다.<br />
                안전 사고 위험이 매우 높습니다.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex justify-between text-[11px] font-black tracking-wider">
              <span className="text-slate-400">보전 단계 준수율</span>
              <span className="text-indigo-400">{progressCount} / {checklist.length} <span className="text-[9px] text-slate-600 ml-0.5">STEPS</span> ({Math.round((progressCount / checklist.length) * 100)}%)</span>
            </div>

            <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.5)] transition-all duration-700 ease-out"
                style={{ width: `${(progressCount / checklist.length) * 100}%` }}
              />
            </div>

            <div className="text-center">
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tight">
                정비 안전 지침 준수 확인 중
              </p>
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes sop-entry {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-sop-entry {
          animation: sop-entry 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .sop-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .sop-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.02);
        }
        .sop-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 2px;
        }
      `}} />
    </div>
  );
};

export default React.memo(MainteSOPChecklist);
